import { cloneElement, isValidElement, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as BlueFilledAlertIcon } from "../../asserts/logo/BlueFilledAlertIcon.svg";
import { ReactComponent as BlueFilledCloseIcon } from "../../asserts/logo/BlueFilledCloseIcon.svg";
import { ReactComponent as KeyboardArrowDownIcon } from "../../asserts/logo/KeyboardArrowDown.svg";
import { ReactComponent as DangerIcon } from "../../asserts/logo/Danger.svg";
import { ReactComponent as CloseIcon } from "../../asserts/logo/close.svg";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import {
  resolveProbeUrl,
  pathLooksLikeFullUrl,
  pathHostDiffersFromBase,
} from "../../utils/probeUrlUtils";
import {
  AUTH_TYPE_OPTIONS,
  ENDPOINT_METHOD_OPTIONS,
  ENDPOINT_PATH_MAX_LENGTH,
  isApiSurfaceInUse,
  methodUsesBody,
  endpointOperationInputValue,
  nextEndpointId,
} from "../../utils/apiSurfaceForm";
import { validateApiBaseUrl } from "../../utils/validateApiBaseUrl";

const F = INTAKE_FONT;
const PRIMARY = "#005BF0";

const fieldLabelSx = {
  fontSize: 13,
  fontWeight: 600,
  color: "#383838",
  fontFamily: F,
  mb: 0.75,
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 14,
    fontFamily: F,
    bgcolor: "#fff",
    borderRadius: "4px",
  },
};

const cardSx = {
  border: `1px solid ${INTAKE_COLORS.g2}`,
  borderRadius: "8px",
  p: { xs: 2, sm: 2.5 },
  bgcolor: "#fff",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

/* Required-field error state — red outline + message under the field (right aligned) */
const requiredErrorSx = {
  "& .MuiOutlinedInput-root.Mui-error fieldset": {
    borderColor: "#DE350B",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-error:hover fieldset": {
    borderColor: "#DE350B",
  },
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset": {
    borderColor: "#DE350B",
    borderWidth: "1px",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    width: "100%",
    height: "auto",
    m: 0,
    mt: "4px",
    textAlign: "right",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#DE350B",
  },
};

/* Merged in only while a field is in the required-error state, so the red
   outline and message win over the field's own outline/helper styling. */
const requiredErrorActiveSx = {
  gap: 0,
  "& .MuiOutlinedInput-root fieldset": {
    borderColor: "#DE350B !important",
    borderWidth: "1px !important",
  },
  "& .MuiFormHelperText-root": {
    width: "100%",
    height: "auto",
    minHeight: 18,
    m: 0,
    mt: "4px",
    textAlign: "right",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#DE350B !important",
  },
};

/* Figma Textfield (API details only) — Size Large / Default */
const apiDetailsFieldWrapSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: "1 1 0",
  minWidth: 0,
  width: "100%",
  height: "auto",
  boxSizing: "border-box",
};

const apiDetailsLabelSx = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "1px",
  width: "100%",
  height: 21,
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
};

const apiDetailsInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
      height: 24,
      boxSizing: "content-box",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: 0,
      color: "#000000",
      "&::placeholder": {
        color: "#707070",
        opacity: 1,
      },
    },
  },
  "& .MuiSelect-select": {
    padding: "8px 10px !important",
    paddingRight: "40px !important",
    height: "24px !important",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
  },
  "& .MuiSelect-icon": {
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    width: 24,
    height: 24,
    position: "absolute",
  },
  ...requiredErrorSx,
};

const endpointLabelSx = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "1px",
  width: "100%",
  height: 21,
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
};

const endpointInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#383838",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&.Mui-error fieldset": {
      borderColor: "#DE350B",
    },
    "& .MuiOutlinedInput-input": {
      height: 24,
      boxSizing: "content-box",
      padding: "8px 10px",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: 0,
      color: "#383838",
      "&::placeholder": {
        color: "#707070",
        opacity: 1,
      },
    },
  },
  "& .MuiSelect-select": {
    height: "24px !important",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
    padding: "8px 40px 8px 10px !important",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#383838",
  },
  "& .MuiSelect-icon": {
    position: "absolute",
    right: 10,
    top: "50%",
    width: 24,
    height: 24,
    transform: "translateY(-50%)",
  },
  ...requiredErrorSx,
};

const endpointPathInputSx = {
  ...endpointInputSx,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& .MuiFormHelperText-root": {
    width: "100%",
    height: 18,
    minHeight: 18,
    m: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#707070",
  },
};

const endpointPurposeInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 86,
    minHeight: 86,
    boxSizing: "border-box",
    alignItems: "flex-start",
    p: 0,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    height: "70px !important",
    boxSizing: "content-box",
    overflow: "auto !important",
    padding: "8px 10px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    "&::placeholder": {
      color: "#707070",
      opacity: 1,
    },
  },
  ...requiredErrorSx,
};

function ApiDetailsFieldLabel({ children, required = false }) {
  return (
    <Typography component="label" sx={apiDetailsLabelSx}>
      {required ? (
        <Box component="span" aria-hidden sx={{ color: "#DE350B" }}>
          *
        </Box>
      ) : null}
      {children}
    </Typography>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <Typography sx={required ? endpointLabelSx : fieldLabelSx} component="label">
      {required ? (
        <Box component="span" aria-hidden sx={{ color: "#DE350B" }}>
          *
        </Box>
      ) : null}
      {children}
    </Typography>
  );
}

/**
 * Controlled API surface declaration (base URL, auth type, endpoints).
 * Layout matches Gate 1 Readiness Figma: API Details card + Endpoints section.
 */
export default function ApiSurfaceDeclarationForm({
  value,
  onChange,
  showIntro = true,
  renderEndpointFooter,
  endpointsHeaderActions = null,
  hideAddEndpoint = false,
  readOnly = false,
  errors = null,
  baseUrlInputRef = null,
}) {
  const form = value;
  const sectionRequired = isApiSurfaceInUse(form);
  const fieldErrors = errors || {};
  const endpointErrors = fieldErrors.endpoints || {};
  const endpoints = form.endpoints || [];
  const [introDismissed, setIntroDismissed] = useState(false);
  const [endpointPendingDelete, setEndpointPendingDelete] = useState(null);
  const [baseUrlBlurError, setBaseUrlBlurError] = useState("");
  const localBaseUrlRef = useRef(null);
  const setBaseUrlRef = (node) => {
    localBaseUrlRef.current = node;
    if (!baseUrlInputRef) return;
    if (typeof baseUrlInputRef === "function") baseUrlInputRef(node);
    else baseUrlInputRef.current = node;
  };
  const baseUrlFilled = Boolean(String(form.baseUrl || "").trim());
  const showIntroAlert = showIntro && !introDismissed && !baseUrlFilled;
  const baseUrlError = fieldErrors.baseUrl || baseUrlBlurError;
  const renderedEndpointsHeaderActions =
    isValidElement(endpointsHeaderActions) &&
    endpointsHeaderActions.props.children === "Test all endpoints"
      ? cloneElement(endpointsHeaderActions, undefined, "Test all Endpoints")
      : endpointsHeaderActions;

  const setForm = (updater) => {
    if (readOnly) return;
    const next = typeof updater === "function" ? updater(form) : updater;
    onChange(next);
  };

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleBaseUrlChange = (e) => {
    setBaseUrlBlurError("");
    updateField("baseUrl")(e);
  };

  const handleBaseUrlBlur = () => {
    if (readOnly) return;
    const result = validateApiBaseUrl(form.baseUrl);
    if (result.value !== String(form.baseUrl ?? "")) {
      setForm((prev) => ({ ...prev, baseUrl: result.value }));
    }
    setBaseUrlBlurError(result.ok ? "" : result.error);
  };

  const updateEndpoint = (id, field, fieldValue) => {
    setForm((prev) => ({
      ...prev,
      endpoints: (prev.endpoints || []).map((ep) =>
        ep.id === id ? { ...ep, [field]: fieldValue } : ep
      ),
    }));
  };

  const addEndpoint = () => {
    setForm((prev) => {
      const list = prev.endpoints || [];
      return {
        ...prev,
        endpoints: [
          ...list,
          {
            id: nextEndpointId(list),
            method: "",
            path: "",
            operation: "",
            purpose: "",
            requestBody: "",
            payloads: [],
          },
        ],
      };
    });
  };

  const removeEndpoint = (id) => {
    setForm((prev) => {
      const list = (prev.endpoints || []).filter((ep) => ep.id !== id);
      return {
        ...prev,
        endpoints:
          list.length > 0
            ? list
            : [
                {
                  id: "ep-1",
                  method: "",
                  path: "",
                  operation: "",
                  purpose: "",
                  requestBody: "",
                  payloads: [],
                },
              ],
      };
    });
  };

  const requestEndpointRemoval = (endpoint, index) => {
    setEndpointPendingDelete({
      id: endpoint.id,
      index,
      path: String(endpoint.path || "").trim(),
    });
  };

  const cancelEndpointRemoval = () => {
    setEndpointPendingDelete(null);
  };

  const confirmEndpointRemoval = () => {
    if (!endpointPendingDelete) return;
    removeEndpoint(endpointPendingDelete.id);
    setEndpointPendingDelete(null);
  };

  const addPayloadVariant = (endpointId) => {
    setForm((prev) => ({
      ...prev,
      endpoints: (prev.endpoints || []).map((ep) => {
        if (ep.id !== endpointId) return ep;
        const payloads = Array.isArray(ep.payloads) ? [...ep.payloads] : [];
        payloads.push({ name: `variant-${payloads.length + 1}`, body: "{}", expectStatus: "" });
        return { ...ep, payloads };
      }),
    }));
  };

  const updatePayloadVariant = (endpointId, index, field, fieldValue) => {
    setForm((prev) => ({
      ...prev,
      endpoints: (prev.endpoints || []).map((ep) => {
        if (ep.id !== endpointId) return ep;
        const payloads = (ep.payloads || []).map((p, i) =>
          i === index ? { ...p, [field]: fieldValue } : p
        );
        return { ...ep, payloads };
      }),
    }));
  };

  const removePayloadVariant = (endpointId, index) => {
    setForm((prev) => ({
      ...prev,
      endpoints: (prev.endpoints || []).map((ep) => {
        if (ep.id !== endpointId) return ep;
        return { ...ep, payloads: (ep.payloads || []).filter((_, i) => i !== index) };
      }),
    }));
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {showIntroAlert && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            width: "100%",
            minWidth: 360,
            height: 74,
            boxSizing: "border-box",
            p: "16px",
            borderRadius: "8px",
            bgcolor: "#F0F6FF",
            border: "none",
            mb: 2.5,
            opacity: 1,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& svg": { width: 32, height: 32, display: "block" },
            }}
          >
            <BlueFilledAlertIcon aria-hidden />
          </Box>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              m: 0,
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "14px",
              lineHeight: "21px",
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: "#0046B8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Add your API base URL, auth type, and endpoints. Choose an auth method to add credentials,
            then use apiAgent to test reachability, authentication, and schema.
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setIntroDismissed(true)}
            sx={{
              p: 0,
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: 0,
              "& svg": { width: 24, height: 24, display: "block" },
            }}
          >
            <BlueFilledCloseIcon />
          </IconButton>
        </Box>
      )}

      {/* API Details — Figma Frame 1171276837 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
          minWidth: 0,
          height: "auto",
          boxSizing: "border-box",
          p: "24px",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          bgcolor: "#FFFFFF",
          mb: 0,
        }}
      >
        {/* Frame 1410120391 — title wrapper */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            width: "fit-content",
            height: "auto",
          }}
        >
          <Typography
            sx={{
              width: "fit-content",
              height: 24,
              m: 0,
              fontFamily: "Lato, sans-serif",
              fontWeight: 900,
              fontStyle: "normal",
              fontSize: "20px",
              lineHeight: "100%",
              letterSpacing: 0,
              color: "#383838",
            }}
          >
            API details
          </Typography>
        </Box>

        {/* Frame 1410120314 — fields row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: "24px",
            width: "100%",
            height: "auto",
          }}
        >
          <Box sx={apiDetailsFieldWrapSx}>
            <ApiDetailsFieldLabel required={sectionRequired}>API base URL</ApiDetailsFieldLabel>
            <TextField
              size="small"
              placeholder="https://api.example.com or http://host:port"
              value={form.baseUrl}
              onChange={handleBaseUrlChange}
              onBlur={handleBaseUrlBlur}
              disabled={readOnly}
              inputRef={setBaseUrlRef}
              inputProps={{ autoComplete: "off", name: "api-base-url" }}
              error={Boolean(baseUrlError)}
              helperText={baseUrlError || ""}
              sx={apiDetailsInputSx}
            />
          </Box>
          <Box sx={{ ...apiDetailsFieldWrapSx, height: "auto", minHeight: 69 }}>
            <ApiDetailsFieldLabel required={sectionRequired}>Authentication (Declared)</ApiDetailsFieldLabel>
            <FormControl size="small" error={Boolean(fieldErrors.authType)} sx={apiDetailsInputSx}>
              <Select
                value={
                  form.authType && AUTH_TYPE_OPTIONS.includes(form.authType)
                    ? form.authType
                    : ""
                }
                onChange={updateField("authType")}
                displayEmpty
                disabled={readOnly}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(selected) => {
                  if (!selected || !AUTH_TYPE_OPTIONS.includes(selected)) {
                    return (
                      <Typography
                        sx={{
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 400,
                          fontStyle: "normal",
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: 0,
                          color: "#707070",
                        }}
                      >
                        Select an authentication Type
                      </Typography>
                    );
                  }
                  return selected;
                }}
              >
                <MenuItem value="" disabled sx={{ display: "none" }}>
                  Select an authentication Type
                </MenuItem>
                {AUTH_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.authType ? (
                <FormHelperText>{fieldErrors.authType}</FormHelperText>
              ) : null}
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Endpoints */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: 80,
          boxSizing: "border-box",
          p: "24px",
          gap: "10px",
          borderRadius: "8px",
          bgcolor: "#FFFFFF",
          mt: "10px",
          mb: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            minWidth: 0,
            height: 32,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "fit-content",
              height: 24,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                width: "fit-content",
                height: 24,
                m: 0,
                fontFamily: "Lato, sans-serif",
                fontWeight: 900,
                fontStyle: "normal",
                fontSize: "20px",
                lineHeight: "100%",
                letterSpacing: 0,
                color: "#383838",
              }}
            >
              Endpoints
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: "#005BF0",
                color: "#FFFFFF",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: 0,
              }}
            >
              {endpoints.length}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "16px",
              minWidth: 0,
              height: 32,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: 32,
                "& > button": {
                  width: "fit-content",
                  height: 32,
                  boxSizing: "border-box",
                  p: "4px 16px",
                  border: "none",
                  borderRadius: "4px",
                  bgcolor: "transparent",
                  color: "#005BF0",
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontSize: "13px",
                  lineHeight: "24px",
                  letterSpacing: 0,
                  textDecoration: "none",
                },
              }}
            >
              {endpoints.length > 1 ? renderedEndpointsHeaderActions : null}
            </Box>
            {!readOnly && !hideAddEndpoint && (
              <Button
                variant="outlined"
                disableElevation
                onClick={addEndpoint}
                sx={{
                  width: "fit-content",
                  minWidth: 112,
                  height: 32,
                  boxSizing: "border-box",
                  p: "4px 16px",
                  gap: "4px",
                  border: "1px solid #005BF0",
                  borderRadius: "4px",
                  bgcolor: "#FFFFFF",
                  color: "#005BF0",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontSize: "13px",
                  lineHeight: "24px",
                  letterSpacing: 0,
                  "&:hover": {
                    border: "1px solid #005BF0",
                    bgcolor: "#FFFFFF",
                    backgroundImage:
                      "linear-gradient(0deg, rgba(0, 91, 240, 0.08), rgba(0, 91, 240, 0.08))",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                  },
                  "&:focus-visible, &.Mui-focusVisible": {
                    border: "2px solid #005FCC",
                    bgcolor: "#FFFFFF",
                    backgroundImage: "none",
                    outline: "none",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                  },
                  "& .MuiTouchRipple-ripplePulsate": {
                    display: "none",
                  },
                  "&:active": {
                    border: "1px solid #005BF0",
                    bgcolor: "#FFFFFF",
                    backgroundImage: "none",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                  },
                  "&.Mui-disabled": {
                    border: "1px solid #E6E6E6",
                    bgcolor: "#F5F5F5",
                    backgroundImage: "none",
                    color: "#BBBBBB",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                Add Endpoint
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Stack spacing={2}>
          {endpoints.map((ep, epIndex) => {
            const epFieldErrors = endpointErrors[ep.id] || {};
            const usesBody = methodUsesBody(ep.method);
            const hasVariants = Array.isArray(ep.payloads) && ep.payloads.length > 0;
            const pathLen = String(ep.path || "").length;
            const pathIsFullUrl = pathLooksLikeFullUrl(ep.path);
            const pathHostMismatch = pathHostDiffersFromBase(form.baseUrl, ep.path);
            const resolvedUrl =
              form.baseUrl && ep.path ? resolveProbeUrl(form.baseUrl, ep.path) : "";
            const pathHelperText = (() => {
              if (pathLen >= ENDPOINT_PATH_MAX_LENGTH - 20) {
                return `${pathLen}/${ENDPOINT_PATH_MAX_LENGTH} ΓÇö URL path only, not request body`;
              }
              if (pathIsFullUrl && pathHostMismatch) {
                return `Full API URL ΓÇö resolves to ${resolvedUrl} (API host differs from base URL).`;
              }
              if (pathIsFullUrl) {
                return `Full URL ΓÇö resolves to ${resolvedUrl}`;
              }
              if (resolvedUrl) {
                return `Resolved URL: ${resolvedUrl}`;
              }
              return "Enter the path only (e.g., /v1/models), not the web app URL";
            })();

            return (
              <Box
                key={ep.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: "100%",
                  minHeight: 370,
                  boxSizing: "border-box",
                  border: "1px solid #E6E6E6",
                  borderRadius: "8px",
                  p: "24px",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%", height: 32, m: 0 }}
                >
                  <Typography
                    sx={{
                      width: "fit-content",
                      height: 19,
                      m: 0,
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 900,
                      fontStyle: "normal",
                      fontSize: "16px",
                      lineHeight: "100%",
                      letterSpacing: 0,
                      color: "#383838",
                    }}
                  >
                    Endpoint - {epIndex + 1}
                  </Typography>
                  {!readOnly && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => requestEndpointRemoval(ep, epIndex)}
                      disabled={endpoints.length <= 1}
                      sx={{
                        width: "fit-content",
                        minWidth: 71,
                        height: 32,
                        boxSizing: "border-box",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        p: "4px 16px",
                        border: "none",
                        borderRadius: "4px",
                        bgcolor: "transparent",
                        color: "#DE350B",
                        fontSize: "13px",
                        fontWeight: 700,
                        lineHeight: "24px",
                        letterSpacing: 0,
                        fontFamily: "Lato, sans-serif",
                        cursor: endpoints.length <= 1 ? "default" : "pointer",
                        "&:hover": {
                          bgcolor: "transparent",
                          textDecoration: "none",
                        },
                      }}
                    >
                      Delete
                    </Box>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    columnGap: "24px",
                    rowGap: "16px",
                    width: "100%",
                    minWidth: 0,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <FieldLabel>Method</FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(epFieldErrors.method)}
                      sx={{
                        ...endpointInputSx,
                        ...(epFieldErrors.method ? requiredErrorActiveSx : null),
                      }}
                    >
                      <Select
                        value={ep.method || ""}
                        onChange={(e) => updateEndpoint(ep.id, "method", e.target.value)}
                        disabled={readOnly}
                        displayEmpty
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={(v) =>
                          v || (
                            <Typography
                              sx={{
                                fontFamily: "Lato, sans-serif",
                                fontWeight: 400,
                                fontStyle: "normal",
                                fontSize: "16px",
                                lineHeight: "24px",
                                letterSpacing: 0,
                                color: "#707070",
                              }}
                            >
                              Select an authentication method
                            </Typography>
                          )
                        }
                      >
                        {ENDPOINT_METHOD_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                      {epFieldErrors.method ? (
                        <FormHelperText>{epFieldErrors.method}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <FieldLabel>Endpoint path</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="/api/v1/health"
                      value={ep.path || ""}
                      onChange={(e) =>
                        updateEndpoint(ep.id, "path", e.target.value.slice(0, ENDPOINT_PATH_MAX_LENGTH))
                      }
                      inputProps={{
                        maxLength: ENDPOINT_PATH_MAX_LENGTH,
                        autoComplete: "off",
                        name: `endpoint-path-${ep.id}`,
                      }}
                      helperText={
                        epFieldErrors.path ? (
                          epFieldErrors.path
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                              width: "100%",
                              height: 18,
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                minWidth: 0,
                                flex: "1 1 auto",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {pathHelperText}
                            </span>
                            <span style={{ flexShrink: 0, textAlign: "right" }}>
                              {pathLen}/{ENDPOINT_PATH_MAX_LENGTH}
                            </span>
                          </Box>
                        )
                      }
                      error={Boolean(epFieldErrors.path) || (pathIsFullUrl && pathHostMismatch)}
                      disabled={readOnly}
                      FormHelperTextProps={{ sx: { m: 0 } }}
                      sx={{
                        ...endpointPathInputSx,
                        ...(epFieldErrors.path ? requiredErrorActiveSx : null),
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      width: "100%",
                      minWidth: 0,
                      gridColumn: "1 / -1",
                    }}
                  >
                    <FieldLabel>Operation</FieldLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Search knowledge base"
                      value={endpointOperationInputValue(ep)}
                      onChange={(e) => updateEndpoint(ep.id, "operation", e.target.value)}
                      inputProps={{ autoComplete: "off", name: `endpoint-operation-${ep.id}` }}
                      disabled={readOnly}
                      error={Boolean(epFieldErrors.operation || epFieldErrors.purpose)}
                      helperText={epFieldErrors.operation || epFieldErrors.purpose || ""}
                      sx={endpointPurposeInputSx}
                    />
                  </Box>
                </Box>

                {usesBody && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${INTAKE_COLORS.g1}` }}>
                    {!hasVariants && (
                      <Box sx={{ mb: 2 }}>
                        <FieldLabel>Request body (JSON)</FieldLabel>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={10}
                          size="small"
                          placeholder='{"prompt": "hello"}'
                          value={ep.requestBody || ""}
                          onChange={(e) => updateEndpoint(ep.id, "requestBody", e.target.value)}
                          disabled={readOnly}
                          sx={{
                            ...inputSx,
                            "& .MuiInputBase-input": {
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                              fontSize: 12,
                            },
                          }}
                        />
                      </Box>
                    )}
                    {!readOnly && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: hasVariants ? 1.5 : 0 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => addPayloadVariant(ep.id)}
                          sx={{ textTransform: "none", fontSize: 12, fontFamily: F }}
                        >
                          Add payload variant
                        </Button>
                        {hasVariants && (
                          <Typography sx={{ fontSize: 11, color: INTAKE_COLORS.g5, fontFamily: F }}>
                            Variants override the single request body when testing.
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {hasVariants &&
                      (ep.payloads || []).map((variant, vi) => (
                        <Box
                          key={`${ep.id}-payload-${vi}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                              md: "minmax(100px, 140px) minmax(72px, 100px) minmax(0, 1fr) auto",
                            },
                            gap: 1.5,
                            mb: 1.5,
                            alignItems: "start",
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <TextField
                            size="small"
                            label="Name"
                            value={variant.name || ""}
                            onChange={(e) => updatePayloadVariant(ep.id, vi, "name", e.target.value)}
                            disabled={readOnly}
                            sx={inputSx}
                          />
                          <TextField
                            size="small"
                            label="Expect HTTP"
                            placeholder="200"
                            value={variant.expectStatus || ""}
                            onChange={(e) =>
                              updatePayloadVariant(ep.id, vi, "expectStatus", e.target.value)
                            }
                            disabled={readOnly}
                            sx={inputSx}
                          />
                          <TextField
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={6}
                            label="Body JSON"
                            value={variant.body || ""}
                            onChange={(e) => updatePayloadVariant(ep.id, vi, "body", e.target.value)}
                            disabled={readOnly}
                            sx={{
                              ...inputSx,
                              "& .MuiInputBase-input": {
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontSize: 12,
                              },
                            }}
                          />
                          {!readOnly && (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => removePayloadVariant(ep.id, vi)}
                              sx={{
                                border: "none",
                                bgcolor: "transparent",
                                color: "#D92D20",
                                fontSize: 12,
                                fontWeight: 700,
                                fontFamily: F,
                                cursor: "pointer",
                                mt: 1,
                                p: 0,
                              }}
                            >
                              Remove
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                )}
                {typeof renderEndpointFooter === "function" ? (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 32,
                      "& > .MuiBox-root:first-of-type": {
                        width: "100%",
                        minHeight: "32px !important",
                        mt: "0 !important",
                        pt: "0 !important",
                        borderTop: "none !important",
                        gap: "16px",
                      },
                      "& > .MuiBox-root:first-of-type .MuiButton-root": {
                        width: "fit-content",
                        minWidth: 136,
                        height: 32,
                        boxSizing: "border-box",
                        p: "4px 16px",
                        border: "1px solid #005BF0",
                        borderRadius: "4px",
                        bgcolor: "#FFFFFF",
                        color: "#005BF0",
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 700,
                        fontSize: "13px",
                        lineHeight: "24px",
                        letterSpacing: 0,
                        "&:hover": {
                          border: "1px solid #005BF0",
                          bgcolor: "#FFFFFF",
                          backgroundImage:
                            "linear-gradient(0deg, rgba(0, 91, 240, 0.08), rgba(0, 91, 240, 0.08))",
                          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                        },
                        "&:focus-visible, &.Mui-focusVisible": {
                          border: "2px solid #005FCC",
                          bgcolor: "#FFFFFF",
                          backgroundImage: "none",
                          outline: "none",
                          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                        },
                        "& .MuiTouchRipple-ripplePulsate": {
                          display: "none",
                        },
                        "&:active": {
                          border: "1px solid #005BF0",
                          bgcolor: "#FFFFFF",
                          backgroundImage: "none",
                          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
                        },
                        "&.Mui-disabled": {
                          border: "1px solid #E6E6E6",
                          bgcolor: "#F5F5F5",
                          backgroundImage: "none",
                          color: "#BBBBBB",
                          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
                        },
                      },
                    }}
                  >
                    {renderEndpointFooter(ep)}
                  </Box>
                ) : null}
              </Box>
            );
          })}
      </Stack>

      <Dialog
        open={Boolean(endpointPendingDelete)}
        onClose={cancelEndpointRemoval}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: "calc(100% - 32px)",
            height: 317,
            maxHeight: "calc(100% - 32px)",
            m: 2,
            borderRadius: "4px",
            bgcolor: "#FFFFFF",
            boxShadow:
              "0px 10px 10px 0px rgba(0, 0, 0, 0.24), 0px 0px 10px 0px rgba(33, 34, 38, 0.12)",
            overflow: "visible",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(0, 0, 0, 0.36)",
            },
          },
        }}
      >
        <IconButton
          type="button"
          onClick={cancelEndpointRemoval}
          aria-label="Close delete endpoint dialog"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            p: 0,
            zIndex: 1,
            "& path": { fill: "#555555" },
          }}
        >
          <CloseIcon style={{ width: 24, height: 24, display: "block" }} />
        </IconButton>

        <DialogContent
          sx={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            p: "40px 64px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            textAlign: "center",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 64,
              height: 64,
              flexShrink: 0,
            }}
          >
            <DangerIcon
              aria-hidden
              style={{
                position: "absolute",
                top: 12.8,
                left: 10.67,
                width: 42.55,
                height: 38.27,
                display: "block",
              }}
            />
          </Box>

          <Typography
            sx={{
              width: "100%",
              maxWidth: 472,
              height: 27,
              m: 0,
              fontFamily: "Lato, sans-serif",
              fontWeight: 900,
              fontSize: "20px",
              lineHeight: "27px",
              letterSpacing: 0,
              color: "#000000",
              textAlign: "center",
            }}
          >
            Delete endpoint?
          </Typography>

          <Typography
            sx={{
              width: "100%",
              maxWidth: 472,
              minHeight: 42,
              m: 0,
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "21px",
              letterSpacing: 0,
              color: "#555555",
              textAlign: "center",
            }}
          >
            This will permanently remove{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Endpoint - {(endpointPendingDelete?.index ?? 0) + 1}
              {endpointPendingDelete?.path ? ` (${endpointPendingDelete.path})` : ""}
            </Box>{" "}
            and its configuration. This action cannot be undone.
          </Typography>

          <Box
            sx={{
              height: 56,
              pt: "16px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={cancelEndpointRemoval}
              sx={{
                width: 77,
                minWidth: 77,
                height: 40,
                minHeight: 40,
                p: "8px 16px",
                gap: "4px",
                boxSizing: "border-box",
                border: "1px solid #005BF0",
                borderRadius: "4px",
                bgcolor: "#FFFFFF",
                color: "#005BF0",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                textTransform: "none",
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                lineHeight: "24px",
                letterSpacing: 0,
                "&:hover": {
                  border: "1px solid #005BF0",
                  bgcolor: "#F0F6FF",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="contained"
              disableElevation
              onClick={confirmEndpointRemoval}
              sx={{
                width: 141,
                minWidth: 141,
                height: 40,
                minHeight: 40,
                p: "8px 16px",
                gap: "4px",
                boxSizing: "border-box",
                border: "1px solid transparent",
                borderRadius: "4px",
                color: "#FFFFFF",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                textTransform: "none",
                whiteSpace: "nowrap",
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                lineHeight: "24px",
                letterSpacing: 0,
                backgroundColor: "transparent",
                backgroundImage:
                  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)",
                "&:hover": {
                  backgroundColor: "transparent",
                  backgroundImage:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Delete Endpoint
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
